import React, { useState, useEffect, useCallback, useRef } from 'react';
import AlienCharacter, { AlienBehavior } from './AlienCharacter';
import AlienMessage from './AlienMessage';
import useIdleDetection from '../../hooks/useIdleDetection';
import './AlienEasterEgg.css';

interface AlienEasterEggProps {
  enabled?: boolean;
  idleThreshold?: number; // Time in ms before first alien appears (default: 30000)
}

// Behavior progression based on idle duration
const BEHAVIOR_THRESHOLDS = {
  peek: 30000,    // 30 seconds
  walk: 60000,    // 1 minute
  sit: 90000,     // 1.5 minutes
  wave: 120000,   // 2 minutes
};

// Timing constants
const SCURRY_DELAY = 1500;      // Wait 1.5 seconds before scurrying away
const SCURRY_DURATION = 1200;   // Scurry animation takes 1.2 seconds

const AlienEasterEgg: React.FC<AlienEasterEggProps> = ({
  enabled = true,
  idleThreshold = 30000,
}) => {
  const [behavior, setBehavior] = useState<AlienBehavior>('hidden');
  const [showMessage, setShowMessage] = useState(false);
  const [isScurrying, setIsScurrying] = useState(false);
  const scurryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleIdle = useCallback(() => {
    if (!enabled) return;
    // Cancel any pending scurry if user goes idle again
    if (scurryTimeoutRef.current) {
      clearTimeout(scurryTimeoutRef.current);
      scurryTimeoutRef.current = null;
    }
    setIsScurrying(false);
    setBehavior('peek');
  }, [enabled]);

  const handleActive = useCallback(() => {
    if (behavior !== 'hidden' && !isScurrying) {
      // Grace period - alien notices you but doesn't immediately run
      scurryTimeoutRef.current = setTimeout(() => {
        setIsScurrying(true);
        setBehavior('scurry');
        setShowMessage(false);
        
        // Hide after scurry animation completes
        setTimeout(() => {
          setBehavior('hidden');
          setIsScurrying(false);
        }, SCURRY_DURATION);
      }, SCURRY_DELAY);
    }
  }, [behavior, isScurrying]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scurryTimeoutRef.current) {
        clearTimeout(scurryTimeoutRef.current);
      }
    };
  }, []);

  const { isIdle, idleDuration } = useIdleDetection({
    idleTime: idleThreshold,
    onIdle: handleIdle,
    onActive: handleActive,
  });

  // Progress through behaviors based on idle duration
  useEffect(() => {
    if (!isIdle || !enabled || isScurrying) return;

    if (idleDuration >= BEHAVIOR_THRESHOLDS.wave) {
      setBehavior('wave');
      setShowMessage(true);
    } else if (idleDuration >= BEHAVIOR_THRESHOLDS.sit) {
      setBehavior('sit');
    } else if (idleDuration >= BEHAVIOR_THRESHOLDS.walk) {
      setBehavior('walk');
    } else if (idleDuration >= BEHAVIOR_THRESHOLDS.peek) {
      setBehavior('peek');
    }
  }, [isIdle, idleDuration, enabled, isScurrying]);

  // Don't render if disabled
  if (!enabled) {
    return null;
  }

  return (
    <div className="alien-easter-egg-container">
      <AlienCharacter 
        behavior={behavior}
        onAnimationComplete={() => {
          if (behavior === 'scurry') {
            setBehavior('hidden');
            setIsScurrying(false);
          }
        }}
      />
      {showMessage && behavior === 'wave' && (
        <AlienMessage onDismiss={() => setShowMessage(false)} />
      )}
    </div>
  );
};

export default AlienEasterEgg;
